import { DateTime } from "luxon";
import ApiHttp from "byzantine/src/ApiHttp";

export type FileFormat = "csv" | "ofx" | "qif" | "qbo" | "qfx";

export interface DownloadParams {
  accountId: string;
  fileFormat: FileFormat;
  minDate?: string;
  maxDate?: string;
  period?: string;
}

/**
 * Formats a date string for API consumption
 */
export const formatDateForApi = (date?: string): string | undefined => {
  if (!date) return undefined;
  const parsed = DateTime.fromFormat(date, "M/d/yyyy");
  return parsed.isValid ? parsed.toFormat("yyyy-MM-dd") : undefined;
};

/**
 * Extracts filename from content-disposition header
 */
export const extractFilenameFromResponse = (
  contentDisposition: string,
  fallbackFormat: FileFormat
): string => {
  try {
    return decodeURI(
      contentDisposition
        ?.split(";")
        ?.find((n: string) => n.includes('filename="'))
        ?.replace("filename=", "")
        ?.replace(/"/g, "")
        ?.trim() || `transactions.${fallbackFormat}`
    );
  } catch {
    return `transactions.${fallbackFormat}`;
  }
};

/**
 * Creates and triggers a file download
 */
export const triggerFileDownload = (data: any, filename: string): void => {
  const tempDownloadUrl = URL.createObjectURL(new Blob([data]));
  const tempLink = document.createElement("a");
  tempLink.href = tempDownloadUrl;
  tempLink.setAttribute("download", filename);
  document.body.appendChild(tempLink);
  tempLink.click();

  // Cleanup
  tempLink.parentNode?.removeChild(tempLink);
  URL.revokeObjectURL(tempDownloadUrl);
};

/**
 * Downloads transaction data for a given account
 */
export const downloadTransactions = async ({
  accountId,
  fileFormat,
  minDate,
  maxDate,
  period,
}: DownloadParams): Promise<void> => {
  const endpoint = `accounts/${accountId}/transactions_download`;

  const payload = {
    file_format: fileFormat,
    period: period || "custom",
    min_date: minDate,
    max_date: maxDate,
  };

  const response = await ApiHttp.fetch(endpoint, { method: "GET" }, payload);

  const filename = extractFilenameFromResponse(
    response.headers["content-disposition"],
    fileFormat
  );

  triggerFileDownload(response.data, filename);
};

/**
 * Downloads transactions for the past year (default behavior)
 */
export const downloadTransactionsForPastYear = async (
  accountId: string,
  fileFormat: FileFormat
): Promise<void> => {
  const oneYearAgo = DateTime.now().minus({ years: 1 }).toFormat("yyyy-MM-dd");
  const today = DateTime.now().toFormat("yyyy-MM-dd");

  return downloadTransactions({
    accountId,
    fileFormat,
    minDate: oneYearAgo,
    maxDate: today,
  });
};
